import { IconButton } from '@mui/material';
import { PaintBrush, Question } from '@phosphor-icons/react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  Background,
  Connection,
  ControlButton,
  Controls,
  OnConnect,
  OnEdgesDelete,
  Panel,
  useReactFlow,
  useStore,
  useStoreApi,
} from 'reactflow';
import styled from 'styled-components';
import { GLOBAL_NODE_ID_PREFIX } from '../../constants/globalNode';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { edgeTypes, nodeTypes } from '../../util/nodeTypes';
import { filterTombstoned } from '../../util/edgeTombstones';
import AgentStateVisual from '../AgentStateVisual/AgentStateVisual';
import ContextMenu, { ContextMenuProps } from '../ContextMenu';
import ColorModal from '../CoreNodeDIsplay/ColorModal';
import { LineTypeControl } from './LineTypeControl';
import MultiEdgeSelectIndicator from './MultiEdgeSelectIndicator';
import MultiSelectIndicator from './MultiSelectIndicator';

interface ExtendNode extends Node {
  id: string;
  position?: {
    x: number;
    y: number;
  };
  selected: boolean;
  data?: any;
  // parentNode: ParentNode | null;
}

const StyledBackground = styled(Background)`
  height: 100vh;
`;

const AnimatedGradient = styled.div<{ background: string }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    inset: -50%;
    background: ${props => props.background};
    will-change: transform;
    animation: gradientDrift 15s ease infinite;
  }

  @keyframes gradientDrift {
    0% {
      transform: translate3d(0, 0, 0);
    }
    50% {
      transform: translate3d(-25%, 0, 0);
    }
    100% {
      transform: translate3d(0, 0, 0);
    }
  }

`;

const proOptions = { hideAttribution: true };

const CANVAS_STYLE_DARK = { background: '#475467' };
const CANVAS_STYLE_LIGHT = { background: '#E7F0FF' };
const CONNECTION_LINE_STYLE_FLOW = { strokeWidth: 7 };
const CONNECTION_LINE_STYLE_DEFAULT = { strokeWidth: 1.5 };
const SNAP_GRID: [number, number] = [15, 15];

const ReactFlowBody = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setReactFlowInstance,
  onConnect,
  onEdgeDelete,
  onEdgeUpdate,
  onEdgeUpdateStart,
  onEdgeUpdateEnd,
  onDragOver,
  onDrop,
  onDragLeave,
  updateNodeEdge,
  setUpdateNodeEdge,
  onNodeDragStop,
  user,
  isValidConnection,
  onAddNode,
  onNodeSelected,
  handlePasteNode,
  handlePasteExternal,
  onMouseMove,
  onDeleteNodes,
  readOnly,
}: any) => {
  const [type, setType] = useState<string>('paste');
  const { lineType, setLineType } = useFlowsStore(s => ({
    lineType: s.lineType,
    setLineType: s.setLineType,
  }));
  const [colors, setColors] = useState(user?.backgroundColor ?? []);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const setAuthUser = useAuthStore(state => state.setAuthUser);

  const theme = user.theme;
  const { setNodes } = useReactFlow();
  const storeApi = useStoreApi();
  // Patch the canvas store's `setEdges` once so every edge write — the
  // controlled-prop sync, the server reconcile, autosave-driven re-renders —
  // funnels through a single tombstone filter. An optimistically deleted edge
  // can then never be resurrected on the canvas before the backend confirms,
  // which is what made deletion only "stick" online after the /edge response.
  useLayoutEffect(() => {
    const original = storeApi.getState().setEdges;
    if ((original as any).__tombstonePatched) return;
    const patched = (eds: any[]) => original(filterTombstoned(eds));
    (patched as any).__tombstonePatched = true;
    storeApi.setState({ setEdges: patched });
  }, [storeApi]);
  // Sync the controlled `edges` prop to ReactFlow's internal Zustand store
  // synchronously (before paint) so the canvas repaints in the same frame the
  // React state commits. The built-in fi() effect does the same sync but via
  // useEffect, which runs after paint and causes a one-frame visual lag on
  // optimistic removals. Routes through the patched (tombstone-filtered) setter.
  useLayoutEffect(() => {
    storeApi.getState().setEdges(edges);
  }, [edges, storeApi]);

  // During a pan/zoom gesture, promote the viewport (which holds every node) to
  // its own GPU layer so the move is pure compositing instead of repainting all
  // node content. The hint is applied only for the duration of the gesture and
  // released on end, to avoid the GPU-memory cost of a permanent layer.
  const viewportRef = useRef<HTMLElement | null>(null);
  const beginPanComposite = useCallback(() => {
    const viewport = document.querySelector<HTMLElement>(
      '.react-flow__viewport',
    );
    viewportRef.current = viewport;
    if (viewport) {
      viewport.style.willChange = 'transform';
    }
  }, []);
  const endPanComposite = useCallback(() => {
    if (viewportRef.current) {
      viewportRef.current.style.willChange = '';
    }
  }, []);
  useEffect(() => {
    return () => {
      if (viewportRef.current) {
        viewportRef.current.style.willChange = '';
      }
    };
  }, []);

  const sourceHandleId = useStore(s => s.connectionStartHandle?.handleId);
  const isFlowHandle =
    ['input', 'outputFlow', 'a'].includes(sourceHandleId ?? '') ||
    ['outputHandle-', 'conditionHandle-'].some(prefix =>
      sourceHandleId?.startsWith(prefix),
    );

  const validNodes = useMemo(
    () => nodes.filter((node: any) => node !== undefined && node !== null),
    [nodes],
  );

  const selectedNodes = useMemo(
    () => validNodes.filter((node: any) => node.selected),
    [validNodes],
  );
  const selectedCount = selectedNodes.length;

  // mousePosition was a useState updated on every mousemove, causing
  // ReactFlowBody (and all 156+ children) to re-render on each pixel of
  // mouse movement. The value was never read — removed entirely.

  const gradientStyle = {
    background:
      colors?.length <= 1
        ? `${colors[0]}`
        : `linear-gradient(-45deg, ${colors.join(', ')})`,
  };

  const [menu, setMenu] = useState<Partial<ContextMenuProps>>({});

  const handleCopyNode = useCallback(
    (node: any) => {
      const selectedNode = validNodes.find((node: ExtendNode) => node.selected);
      if (selectedNode) {
        if (selectedNode.id && selectedNode.id.startsWith(excludedIdPrefix)) {
          return;
        }

        const { text, previewResponses, ...rest } = selectedNode;

        const nodeToCopy = {
          ...rest,
          data: {
            ...rest.data,
            text: null,
            previewResponses: null,
            promptData: null,
            previewIndex: 0,
          },
        };

        setType('copy');
        onNodeSelected(nodeToCopy);
      } else {
        console.log('No node selected.');
      }
    },
    [validNodes, onNodeSelected],
  );

  const excludedIdPrefix = GLOBAL_NODE_ID_PREFIX;

  const handleCancelSelection = useCallback(() => {
    setNodes((currentNodes: any[]) =>
      currentNodes.map((node: any) => ({
        ...node,
        selected: false,
      })),
    );
  }, [setNodes]);

  // Stable callbacks — defined once, never recreated.
  // Inline arrow functions here were causing every ReactFlow child (edges,
  // handles, controls) to re-render on every ReactFlowBody render.
  const handleEdgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleContextMenuPaste = useCallback(() => {
    console.log('Pasting node reactflowbody');
  }, []);

  const handleContextMenuDelete = useCallback(() => {
    console.log('Deleting node');
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (readOnly) return;

      const activeElement = document.activeElement;
      const isInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      if (!isInput) {
        if (event.key === 'c' && event.metaKey) {
          handleCopyNode(event);
        } else if (event.key === 'v' && event.metaKey) {
          handlePasteNode(event);
        } else if (event.key === 'Escape' && selectedCount > 1) {
          event.preventDefault();
          handleCancelSelection();
        } else if (
          event.key === 'Delete' &&
          selectedCount > 0 &&
          onDeleteNodes
        ) {
          event.preventDefault();
          const nodesToDelete = selectedNodes.filter(
            (node: any) => !node.id?.startsWith(excludedIdPrefix),
          );
          if (nodesToDelete.length > 0) {
            onDeleteNodes(nodesToDelete);
          }
        }
      }
    },
    [
      readOnly,
      handleCopyNode,
      handlePasteNode,
      selectedCount,
      selectedNodes,
      onDeleteNodes,
      handleCancelSelection,
    ],
  );

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      const activeElement = document.activeElement;
      const isInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      if (!isInput && handlePasteExternal) {
        const wasExternalFilePaste = await handlePasteExternal(event);
        if (wasExternalFilePaste) {
          event.preventDefault();
        }
      }
    },
    [handlePasteExternal],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('paste', handlePaste);
    };
  }, [handleKeyDown, handlePaste]);

  // CLICK NODE CLICK
  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: any) => {
      const target = event.target as HTMLElement;

      const element = target.closest('[role="presentation"]');

      const isModal = target.getAttribute('role') === 'presentation';
      const modalContent = target.closest('.MuiDialogContent-root');

      const isInputOrTextarea =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      if (isModal || isInputOrTextarea || element || modalContent) {
        setMenu({});
        setType('');
        onNodeSelected(null);
        return; // Allow the default context menu
      }
      event.preventDefault(); // Prevent default browser menu

      setType('copy');
      onNodeSelected(node);
      setMenu({
        id: node.id,
        top: event.clientY - 4,
        left: event.clientX - 4,
        type: 'copy',
      });
    },
    [onNodeSelected],
  );

  const onPaneClick = useCallback(() => {
    setMenu(prev => (Object.keys(prev).length ? {} : prev));
  }, []);

  return (
    <>
      {readOnly && (
        <style>{`
        .reactflow-wrapper .react-flow__node textarea,
        .reactflow-wrapper .react-flow__node input,
        .reactflow-wrapper .react-flow__node select,
        .reactflow-wrapper .react-flow__node [contenteditable="true"] {
          pointer-events: none !important;
          user-select: none !important;
        }
      `}</style>
      )}
      <ReactFlow
        style={theme === 'dark' ? CANVAS_STYLE_DARK : CANVAS_STYLE_LIGHT}
        // onlyRenderVisibleElements
        connectionLineStyle={
          isFlowHandle
            ? CONNECTION_LINE_STYLE_FLOW
            : CONNECTION_LINE_STYLE_DEFAULT
        }
        className={theme === 'dark' ? 'dark-theme' : 'light-theme'}
        snapToGrid
        snapGrid={SNAP_GRID}
        nodes={validNodes}
        edges={edges}
        connectionLineType={lineType}
        onNodesChange={changes => {
          if (updateNodeEdge.initial) {
            onNodesChange(changes);

            return setUpdateNodeEdge({
              ...updateNodeEdge,
              initial: false,
            });
          }
          if (changes[0].type !== 'remove') {
            onNodesChange(changes);
          }

          if (changes[0].type === 'remove') {
          }
        }}
        onEdgeClick={handleEdgeClick}
        onEdgesChange={e => {
          onEdgesChange(e);
        }}
        onEdgesDelete={onEdgeDelete as OnEdgesDelete}
        onEdgeUpdate={onEdgeUpdate}
        onEdgeUpdateStart={onEdgeUpdateStart}
        onEdgeUpdateEnd={onEdgeUpdateEnd}
        onConnect={onConnect as OnConnect}
        nodeTypes={nodeTypes as any}
        onPointerMove={onMouseMove}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
        onInit={setReactFlowInstance}
        onMoveStart={beginPanComposite}
        onMoveEnd={endPanComposite}
        isValidConnection={(params: Connection) =>
          isValidConnection(params) as boolean
        }
        onNodeDragStop={(event, node) => {
          onNodeDragStop(node);
          setUpdateNodeEdge({
            type: 'position',
            id: node.id,
            data: node.position,
            initial: false,
          });
        }}
        proOptions={proOptions}
        fitView
        edgeTypes={edgeTypes as any}
        elevateEdgesOnSelect
        minZoom={0.03}
        onPaneClick={onPaneClick}
        onNodeContextMenu={readOnly ? undefined : onNodeContextMenu}
        multiSelectionKeyCode={['Control', 'Shift']}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        edgesUpdatable={!readOnly}
      >
        <AgentStateVisual />
        <Controls
          position='bottom-right'
          style={{
            display: 'flex',
            background: '#fff',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: '5px',
            padding: '8px',
            gap: '24px',
          }}
        >
          <ControlButton
            onClick={() => {
              setColorModalOpen(true);
            }}
          >
            <PaintBrush style={{}} size={20} />
          </ControlButton>

          <LineTypeControl lineType={lineType} setLineType={setLineType} />

          <IconButton
            id='my_custom_link'
            onClick={() => {
              // setColorModalOpen(true);
            }}
          >
            <Question weight='fill' color='#000000' size={20} />
          </IconButton>
        </Controls>

        <Panel position='top-right'>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MultiSelectIndicator
              selectedCount={selectedCount}
              selectedNodes={selectedNodes}
              onDeleteNodes={onDeleteNodes}
              onCancelSelection={handleCancelSelection}
            />
            <MultiEdgeSelectIndicator />
          </div>
        </Panel>
        <ColorModal
          colorModalOpen={colorModalOpen}
          setColorModalOpen={setColorModalOpen}
          colors={colors}
          setColors={setColors}
          setAuthUser={setAuthUser}
        />

        {/* <MiniMapStyled /> */}
        <AnimatedGradient background={gradientStyle.background} />
        <StyledBackground gap={12} size={1} />

        {menu && (
          <ContextMenu
            id={menu.id as string}
            top={menu.top || 0}
            left={menu.left || 0}
            setMenu={setMenu}
            onCopy={handleCopyNode}
            onAddNode={onAddNode}
            onPaste={handleContextMenuPaste}
            onDelete={handleContextMenuDelete}
            type={type}
            setType={setType}
          />
        )}
      </ReactFlow>
    </>
  );
};

export default ReactFlowBody;
